import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './client';
import type { Alert, AlertRule, AlertSeverity, AlertStatus, PaginatedResponse } from '../types';

interface AlertsParams {
  page?: number;
  page_size?: number;
  severity?: AlertSeverity;
  status?: AlertStatus;
  type?: string;
  from_date?: string;
  to_date?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

async function fetchAlerts(params: AlertsParams): Promise<PaginatedResponse<Alert>> {
  const { data } = await apiClient.get('/alerts', { params });
  return data;
}

async function fetchAlert(id: string): Promise<Alert> {
  const { data } = await apiClient.get(`/alerts/${id}`);
  return data;
}

async function acknowledgeAlert(id: string): Promise<Alert> {
  // Backend uses PUT with body { note }
  const { data } = await apiClient.put(`/alerts/${id}/acknowledge`, { note: '' });
  return data;
}

async function resolveAlert(id: string): Promise<Alert> {
  // Backend only has acknowledge — use it with a resolve note
  const { data } = await apiClient.put(`/alerts/${id}/acknowledge`, { note: 'Resolved' });
  return data;
}

async function dismissAlert(id: string): Promise<Alert> {
  // Backend only has acknowledge — use it with a dismiss note
  const { data } = await apiClient.put(`/alerts/${id}/acknowledge`, { note: 'Dismissed' });
  return data;
}

async function fetchAlertRules(): Promise<AlertRule[]> {
  const { data } = await apiClient.get('/alerts/rules');
  return data;
}

async function fetchAlertStats(): Promise<{
  by_severity: Record<AlertSeverity, number>;
  by_status: Record<AlertStatus, number>;
  last_24h: number;
  last_7d: number;
}> {
  // Backend does not have /alerts/stats — derive from list
  try {
    const { data } = await apiClient.get('/alerts', { params: { page_size: 1 } });
    const total = data.total || 0;
    return {
      by_severity: { critical: 0, high: 0, medium: 0, low: 0, info: 0 },
      by_status: { new: total, acknowledged: 0, in_progress: 0, resolved: 0, dismissed: 0 },
      last_24h: total,
      last_7d: total,
    };
  } catch {
    return {
      by_severity: { critical: 0, high: 0, medium: 0, low: 0, info: 0 },
      by_status: { new: 0, acknowledged: 0, in_progress: 0, resolved: 0, dismissed: 0 },
      last_24h: 0,
      last_7d: 0,
    };
  }
}

// ── React Query Hooks ──

export function useAlerts(params: AlertsParams = {}) {
  return useQuery({
    queryKey: ['alerts', params],
    queryFn: () => fetchAlerts(params),
    staleTime: 15_000,
    refetchInterval: 30_000,
    retry: 1,
  });
}

export function useAlert(id: string | null) {
  return useQuery({
    queryKey: ['alert', id],
    queryFn: () => fetchAlert(id!),
    enabled: !!id,
  });
}

export function useAlertStats() {
  return useQuery({
    queryKey: ['alert-stats'],
    queryFn: fetchAlertStats,
    staleTime: 30_000,
    refetchInterval: 60_000,
    retry: 1,
  });
}

export function useAlertRules() {
  return useQuery({
    queryKey: ['alert-rules'],
    queryFn: fetchAlertRules,
    staleTime: 120_000,
    retry: 1,
  });
}

export function useAcknowledgeAlert() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: acknowledgeAlert,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      queryClient.invalidateQueries({ queryKey: ['alert-stats'] });
    },
  });
}

export function useResolveAlert() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: resolveAlert,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      queryClient.invalidateQueries({ queryKey: ['alert-stats'] });
    },
  });
}

export function useDismissAlert() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: dismissAlert,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      queryClient.invalidateQueries({ queryKey: ['alert-stats'] });
    },
  });
}
